import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import boostPools from "src/constants/boostPools"
import { useAction } from "src/hooks/useAction"
import {
  actions,
  fetchFerroBoostPoolDataAsync,
  fetchFerroBoostUserDataAsync,
} from "src/state/xFer"
import { formatNumber } from "src/utils/parseStringToBigNumber"
import { useVaultsApr } from "../../hooks/useVaultsApr"
import useFerroBoostHandlers from "../../hooks/useFerroBoostHandlers"
import ConfirmStep from "./ConfirmStep"
import DepositDetail from "./DepositDetail"
import InputStep from "./InputStep"
import SelectPeriodStep from "./SelectPeriodStep"
import EmptyView from "./EmptyView"
import DrawerModalWrapper from "src/components/DrawerModalWrapper"
import { enqueueToast } from "src/components/Toastify"
import { usePoolTokenBalances } from "src/state/wallet/hooks"
import { useDispatch } from "react-redux"
import { useActiveWeb3React } from "src/hooks"
import { XFER } from "src/constants"
import { TransactionSummaryData } from "src/components/TransactionSummary"
import { BIG_ZERO } from "src/constants/bigNumber"
import { formatBNToString } from "src/utils"
import { useUserFerroBoost } from "src/state/xFer/hooks"
import { useStakePartFer } from "src/hooks/useStakePartFer"
import useXFerState from "../../hooks/useXFerState"

type Props = {
  defaultSelectedPool: number | null
}

const DepositModal = ({ defaultSelectedPool }: Props) => {
  const { t } = useTranslation()
  const [step, setStep] = useState<number>(defaultSelectedPool !== null ? 1 : 0)
  const [selectedPool, setSelectedPool] = useState(defaultSelectedPool)
  const [value, setValue] = useState("")
  const vaultsApr = useVaultsApr()
  const { account } = useActiveWeb3React()
  const { onApprove, onDeposit } = useFerroBoostHandlers()
  const { boostData } = useUserFerroBoost()
  const { allowance } = boostData
  const dispatch = useDispatch()

  const closeDepositModal = useAction(actions.setOpenedDepositModal, null)
  const { openedDepositModal } = useXFerState()
  const harvest = openedDepositModal?.harvest
  const lockAmount = openedDepositModal?.lockAmount
  const { claimAndStake, xFerAmount } = useStakePartFer(
    lockAmount ?? BIG_ZERO,
    harvest,
  )

  const tokenBalances = usePoolTokenBalances()
  const balance = tokenBalances?.[XFER.symbol]
  const emptyCheck = harvest === undefined && balance?.lte(0) && step !== 2

  const isEnabled = allowance && allowance.gt("0")
  const checkAndApprove = useCallback(async () => {
    try {
      if (!isEnabled) {
        await onApprove()
        if (account) {
          dispatch(fetchFerroBoostUserDataAsync(account))
        }
      }
    } catch (e) {
      console.error(e)
      enqueueToast("error", t("xFER is not approved"), t("Transaction Failed"))
    }
  }, [account, dispatch, isEnabled, onApprove, t])

  const handleClaimAndStake = useCallback(async () => {
    try {
      if (claimAndStake) await claimAndStake()
      if (account) {
        dispatch(fetchFerroBoostUserDataAsync(account))
        dispatch(fetchFerroBoostPoolDataAsync())
      }
    } catch (e) {
      closeDepositModal()
    }
  }, [account, closeDepositModal, dispatch, claimAndStake])

  const goNext = useCallback(() => {
    setStep((state) => state + 1)
  }, [])
  const onPeriodSelect = useCallback(
    async (pid: number) => {
      setSelectedPool(pid)
      if (harvest !== undefined) {
        await handleClaimAndStake()
        setValue(formatBNToString(xFerAmount.current, XFER.decimals))
        setStep(2)
      } else {
        setStep(1)
      }
    },
    [handleClaimAndStake, harvest, xFerAmount],
  )

  const summary: TransactionSummaryData = useMemo(
    () => [
      {
        label: t("Deposit"),
        token: XFER,
        amount: formatNumber(value, 2, 6),
      },
    ],
    [t, value],
  )

  const poolApr = formatNumber(
    vaultsApr.find((pool) => pool.poolId === selectedPool)?.apr || "0",
    2,
    2,
  )
  const selectedPoolInfo = boostPools.find((pool) => pool.pid === selectedPool)
  const title = emptyCheck
    ? t("No xFER found")
    : step === 0
    ? t("Select Locking Period")
    : step === 1
    ? t("Deposit xFER")
    : t("Confirm Deposit")

  const handleConfirmClick = useCallback(async () => {
    try {
      if (selectedPool !== null) {
        await checkAndApprove()
        await onDeposit(selectedPool.toString(), value, summary)
      }
      if (account) {
        dispatch(fetchFerroBoostUserDataAsync(account))
        dispatch(fetchFerroBoostPoolDataAsync())
      }
    } catch (e) {
      console.error(e)
      enqueueToast(
        "error",
        t("Your xFER amount has been reverted"),
        t("xFER Deposit in Vault Failed"),
      )
    }
    closeDepositModal()
  }, [
    account,
    checkAndApprove,
    closeDepositModal,
    dispatch,
    onDeposit,
    selectedPool,
    summary,
    t,
    value,
  ])

  useEffect(() => {
    if (harvest !== undefined && step === 2) {
      handleConfirmClick().catch(console.error)
    }
  }, [harvest, step, handleConfirmClick])

  return (
    <DrawerModalWrapper open onClose={closeDepositModal} title={title}>
      {emptyCheck ? (
        <EmptyView onClose={closeDepositModal} />
      ) : (
        <>
          {step === 0 && <SelectPeriodStep onSelect={onPeriodSelect} />}
          {step === 1 && selectedPoolInfo && (
            <InputStep
              onConfirm={goNext}
              remark={
                <DepositDetail
                  apr={poolApr}
                  multiplier={selectedPoolInfo.multiplier}
                  lockPeriod={selectedPoolInfo.lockPeriod}
                />
              }
              value={value}
              onChange={setValue}
            />
          )}
          {step === 2 && selectedPoolInfo && (
            <ConfirmStep
              onConfirm={handleConfirmClick}
              remark={
                <DepositDetail
                  apr={poolApr}
                  multiplier={selectedPoolInfo?.multiplier}
                  lockPeriod={selectedPoolInfo.lockPeriod}
                  hideText
                />
              }
              value={value}
            />
          )}
        </>
      )}
    </DrawerModalWrapper>
  )
}

export default DepositModal
